// Reviewed article translations. Visitor reads are cache-only and missing
// translations never create jobs or invoke paid services.

import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";

export const SUPPORTED_LANGS = ["en", "fr", "es", "pt", "de", "sv", "hi", "zh", "ru", "ar"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

export const LANG_LABELS: Record<SupportedLang, string> = {
  en: "English",
  fr: "Français",
  es: "Español",
  pt: "Português",
  de: "Deutsch",
  sv: "Svenska",
  hi: "हिन्दी",
  zh: "中文",
  ru: "Русский",
  ar: "العربية",
};

export const LANG_FULL: Record<SupportedLang, string> = {
  en: "English",
  fr: "French",
  es: "Spanish",
  pt: "Brazilian Portuguese",
  de: "German",
  sv: "Swedish",
  hi: "Hindi",
  zh: "Simplified Chinese",
  ru: "Russian",
  ar: "Modern Standard Arabic",
};

const Input = z.object({
  slug: z.string().min(1),
  lang: z.enum(SUPPORTED_LANGS),
  cacheOnly: z.boolean().optional().default(false),
});

function serverClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(process.env.SUPABASE_URL!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`)
          h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export const getDiscoveryTranslation = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => Input.parse(input))
  .handler(async ({ data }) => {
    const { slug, lang, cacheOnly } = data;
    if (lang === "en") return null;

    const sb = serverClient();
    const { data: d, error: dErr } = await (sb as any)
      .from("discoveries")
      .select("id, title, excerpt, body, audio_script")
      .eq("slug", slug)
      .eq("is_published", true)
      .maybeSingle();
    if (dErr) throw new Error(dErr.message);
    if (!d) return null;

    // 1. cache hit
    const { data: cached } = await (sb as any)
      .from("discovery_translations")
      .select("title, excerpt, body, audio_script, review_status")
      .eq("discovery_id", d.id)
      .eq("lang", lang)
      .maybeSingle();
    // Only translations that passed the native-grammar review are shown.
    // Everything else stays in English until the catalogue job approves it.
    if (cached && cached.review_status === "approved") {
      return {
        lang,
        title: cached.title,
        excerpt: cached.excerpt,
        body: cached.body,
        audio_script: cached.audio_script,
      };
    }

    // Permanent zero-cost boundary: a page request is read-only. Missing copy
    // is handled by the curated UI catalogue or free in-browser fallback.
    void cacheOnly;
    return null;
  });
